import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { throwApiError } from "@src/packages/api/api-error";
import { useApiClient } from "@src/packages/api/louise-api";
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

type EmbryoFreezingPlansPath =
  paths["/patients/{patientId}/embryo-freezing-plans"];
type EmbryoFreezingPlanVersionPath =
  paths["/patients/{patientId}/embryo-freezing-plans/{planId}/versions"];
type EmbryoFreezingPlanAcceptPath =
  paths["/patients/{patientId}/embryo-freezing-plans/{planId}/accept"];
type EmbryoFreezingPlanCancelPath =
  paths["/patients/{patientId}/embryo-freezing-plans/{planId}/cancel"];
type EmbryoFreezingPlanSnapshotPath =
  paths["/patients/{patientId}/embryo-freezing-plans/{planId}/cycle-snapshots"];

export type EmbryoFreezingPlan =
  EmbryoFreezingPlansPath["get"]["responses"][200]["content"]["application/json"]["data"][number];
export type CreateEmbryoFreezingPlanInput = RequestBody<
  EmbryoFreezingPlansPath["post"]
>;
export type CreateEmbryoFreezingPlanVersionInput = RequestBody<
  EmbryoFreezingPlanVersionPath["post"]
>;
export type AcceptEmbryoFreezingPlanInput = RequestBody<
  EmbryoFreezingPlanAcceptPath["post"]
>;
export type CancelEmbryoFreezingPlanInput = RequestBody<
  EmbryoFreezingPlanCancelPath["post"]
>;
export type SnapshotEmbryoFreezingPlanInput = RequestBody<
  EmbryoFreezingPlanSnapshotPath["post"]
>;

export const embryoFreezingPlansQueryKey = (patientId: string | null) =>
  ["embryo-freezing-plans", patientId] as const;

export const embryoFreezingPlanQueryKey = (
  patientId: string | null,
  planId: string | null,
) => ["embryo-freezing-plan", patientId, planId] as const;

function createClientRequestId() {
  const runtimeCrypto = globalThis.crypto;
  if (runtimeCrypto?.randomUUID) return runtimeCrypto.randomUUID();

  const hex = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
  return `${hex()}${hex()}${hex()}${hex()}-${hex()}-4${hex().slice(1)}-8${hex().slice(1)}-${hex()}${hex()}${hex()}`;
}

function requireResponseData<T>(
  data: { data: T } | undefined,
  error: unknown,
  fallback: string,
): T {
  if (data) return data.data;
  throwApiError(error, fallback);
}

export function useEmbryoFreezingPlans() {
  const { client, patientId } = useApiClient();
  const queryKey = embryoFreezingPlansQueryKey(patientId);

  return useQuery<EmbryoFreezingPlan[]>({
    queryKey,
    queryFn: async () => {
      if (!patientId) return [];
      const { data, error } = await client.GET(
        "/patients/{patientId}/embryo-freezing-plans",
        { params: { path: { patientId } } },
      );
      return requireResponseData(
        data,
        error,
        "Failed to load embryo-freezing plans",
      );
    },
    enabled: !!patientId,
  });
}

export function useEmbryoFreezingPlan(planId: string | null | undefined) {
  const { client, patientId } = useApiClient();
  const normalizedPlanId = planId ?? null;

  return useQuery<EmbryoFreezingPlan>({
    queryKey: embryoFreezingPlanQueryKey(patientId, normalizedPlanId),
    queryFn: async () => {
      if (!patientId || !normalizedPlanId) {
        throw new Error("patientId and planId are required");
      }
      const { data, error } = await client.GET(
        "/patients/{patientId}/embryo-freezing-plans/{planId}",
        { params: { path: { patientId, planId: normalizedPlanId } } },
      );
      return requireResponseData(
        data,
        error,
        "Failed to load embryo-freezing plan",
      );
    },
    enabled: !!patientId && !!normalizedPlanId,
  });
}

function usePlanMutationInvalidation() {
  const queryClient = useQueryClient();

  return (patientId: string, planId?: string) => {
    void queryClient.invalidateQueries({
      queryKey: embryoFreezingPlansQueryKey(patientId),
    });
    if (planId) {
      void queryClient.invalidateQueries({
        queryKey: embryoFreezingPlanQueryKey(patientId, planId),
      });
    }
  };
}

export function useCreateEmbryoFreezingPlan() {
  const { client, patientId } = useApiClient();
  const invalidate = usePlanMutationInvalidation();

  return useMutation({
    mutationFn: async (input: CreateEmbryoFreezingPlanInput) => {
      if (!patientId) throw new Error("patientId is required");
      const { data, error } = await client.POST(
        "/patients/{patientId}/embryo-freezing-plans",
        { params: { path: { patientId } }, body: input },
      );
      return requireResponseData(
        data,
        error,
        "Failed to create embryo-freezing plan",
      );
    },
    onSuccess: () => {
      if (patientId) invalidate(patientId);
    },
  });
}

export function useCreateEmbryoFreezingPlanVersion() {
  const { client, patientId } = useApiClient();
  const invalidate = usePlanMutationInvalidation();

  return useMutation({
    mutationFn: async ({
      planId,
      input,
    }: {
      planId: string;
      input: CreateEmbryoFreezingPlanVersionInput;
    }) => {
      if (!patientId) throw new Error("patientId is required");
      const { data, error } = await client.POST(
        "/patients/{patientId}/embryo-freezing-plans/{planId}/versions",
        { params: { path: { patientId, planId } }, body: input },
      );
      return requireResponseData(
        data,
        error,
        "Failed to save embryo-freezing plan version",
      );
    },
    onSuccess: (plan, { planId }) => {
      if (patientId) invalidate(patientId, planId);
    },
  });
}

export function useAcceptEmbryoFreezingPlan() {
  const { client, patientId } = useApiClient();
  const invalidate = usePlanMutationInvalidation();

  return useMutation({
    mutationFn: async ({
      planId,
      input,
    }: {
      planId: string;
      input: AcceptEmbryoFreezingPlanInput;
    }) => {
      if (!patientId) throw new Error("patientId is required");
      const { data, error } = await client.POST(
        "/patients/{patientId}/embryo-freezing-plans/{planId}/accept",
        { params: { path: { patientId, planId } }, body: input },
      );
      return requireResponseData(
        data,
        error,
        "Failed to accept embryo-freezing plan",
      );
    },
    onSuccess: (plan, { planId }) => {
      if (patientId) invalidate(patientId, planId);
    },
  });
}

export function useCancelEmbryoFreezingPlan() {
  const { client, patientId } = useApiClient();
  const invalidate = usePlanMutationInvalidation();

  return useMutation({
    mutationFn: async ({
      planId,
      input,
    }: {
      planId: string;
      input: CancelEmbryoFreezingPlanInput;
    }) => {
      if (!patientId) throw new Error("patientId is required");
      const { data, error } = await client.POST(
        "/patients/{patientId}/embryo-freezing-plans/{planId}/cancel",
        { params: { path: { patientId, planId } }, body: input },
      );
      return requireResponseData(
        data,
        error,
        "Failed to cancel embryo-freezing plan",
      );
    },
    onSuccess: (plan, { planId }) => {
      if (patientId) invalidate(patientId, planId);
    },
  });
}

export function useSnapshotEmbryoFreezingPlan() {
  const { client, patientId } = useApiClient();
  const invalidate = usePlanMutationInvalidation();

  return useMutation({
    mutationFn: async ({
      planId,
      input,
    }: {
      planId: string;
      input: SnapshotEmbryoFreezingPlanInput;
    }) => {
      if (!patientId) throw new Error("patientId is required");
      const { data, error } = await client.POST(
        "/patients/{patientId}/embryo-freezing-plans/{planId}/cycle-snapshots",
        { params: { path: { patientId, planId } }, body: input },
      );
      return requireResponseData(
        data,
        error,
        "Failed to snapshot embryo-freezing plan",
      );
    },
    onSuccess: (plan, { planId }) => {
      if (patientId) invalidate(patientId, planId);
    },
  });
}

export { createClientRequestId };
